import { useEffect, useState } from 'react';

import {
  NavigationType,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router';

import { skipToken, useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import {
  ContentReference,
  getResourceOptions,
  getResourceQueryKey,
  getSqsslOptions,
  Resource,
} from '@cfa/pathway-fe-shared/xp';

import { contentReferenceToDocument } from './helpers';

import {
  documentAtom,
  type SetDocumentActionPayload,
  setDocumentAtom,
  updateDocumentAtom,
} from '@/atoms/document';
import { userPrefsAtom } from '@/atoms/userPrefs';

const documentParamsSchema = z.object({
  documentId: z.string(),
  isCompliance: z
    .string()
    .default('false')
    .transform(val => val === 'true'),
});

/** Document params coming from route. */
export const useDocumentParams = () => {
  const params = useParams();
  const location = useLocation();
  const search = () => {
    const queryParams = new URLSearchParams(location.search);
    const searchParams: Record<string, string> = {};
    queryParams.forEach((value, key) => {
      searchParams[key] = value;
    });
    return searchParams;
  };

  const parsedParams = documentParamsSchema.safeParse({
    ...params,
    ...search(),
  });

  if (!parsedParams.success) {
    throw new Error(
      `Document Route Params Error: ${JSON.stringify(z.treeifyError(parsedParams.error))}`,
    );
  }

  return parsedParams.data;
};

/**
 * Router state from Explore search. `searchReferences` lists the variants GET
 * /search returned for the clicked id, for when GET /resources returns fewer
 * (PATHWAY-3371; drop once PATHWAY-4127 aligns the two endpoints).
 */
export interface DocumentRouteState {
  searchReferences?: ContentReference[];
}

const getSearchReferences = (state: unknown): ContentReference[] => {
  if (typeof state !== 'object' || state === null) {
    return [];
  }
  const { searchReferences } = state as DocumentRouteState;
  return Array.isArray(searchReferences) ? searchReferences : [];
};

/** Apply a content reference to documentAtom immediately (no effect deferral). */
const applyDocumentSelection = (
  reference: ContentReference,
  resource: Resource,
  routeDocumentId: string,
  setDocument: (payload: SetDocumentActionPayload) => void,
  setSelection: (selection: {
    reference: ContentReference;
    resource: Resource;
  }) => void,
) => {
  const document = contentReferenceToDocument(
    reference,
    routeDocumentId,
    resource,
  );

  document.type = resource.type;

  setSelection({ reference, resource });
  setDocument({
    document,
    icon: resource.icon,
    country: reference.country,
  });
};

/** Select and find document or fetch document if none in state. */
export const useDocument = (routeDocumentId: string, isCompliance: boolean) => {
  const { chooseDocumentVersion } = useFlags();
  // PUSH = SPA link click; POP = hard reload or direct URL load
  const navigationType = useNavigationType();
  const isSpaNavigation = navigationType === NavigationType.Push;
  const location = useLocation();
  const { search } = location;
  const routeState: unknown = location.state;
  // Opt-in for naked `/doc/:id` deep links (e.g. banner Learn more) that do
  // not pre-hydrate a version — keep SPA suppression for browse/search.
  const forceChooseDocumentVersion =
    new URLSearchParams(search).get('chooseVersion') === 'true';
  const [selection, setSelection] = useState<{
    reference: ContentReference;
    resource: Resource;
  }>();
  const documentState = useAtomValue(documentAtom);
  const { country: userCountry } = useAtomValue(userPrefsAtom);
  // Also fetch when chooseVersion opts into the chooser on SPA Push — otherwise
  // a matching documentAtom id skips the resource load and activeReferences
  // stays empty, so the modal never opens.
  const needFetchResource =
    !isSpaNavigation ||
    forceChooseDocumentVersion ||
    documentState.document?.id !== routeDocumentId;

  const setDocument = useSetAtom(setDocumentAtom);
  const updateDocument = useSetAtom(updateDocumentAtom);
  const { resource, error, isFetching } = useDocumentResource(
    routeDocumentId,
    needFetchResource && !isCompliance,
  );

  /**
   * temporary flag to control whether or not filter references to user country
   * (if endpoint always return all country resources for admin)
   **/
  const showAllReferencesForChooser = chooseDocumentVersion;

  const searchReferences = forceChooseDocumentVersion
    ? getSearchReferences(routeState)
    : [];

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reference list derived from resource + chooser flags
  const activeReferences = !resource?.references
    ? []
    : searchReferences.length > 1
      ? searchReferences
      : showAllReferencesForChooser
        ? resource.references
        : resource.references.filter(
            ({ country }) => country === userCountry.id,
          );

  // auto choose reference if only one
  useEffect(() => {
    if (!resource?.references) {
      return;
    }

    if (activeReferences.length > 1) {
      updateDocument({ icon: resource.icon });
    }

    if (activeReferences.length === 1 && activeReferences[0]) {
      applyDocumentSelection(
        activeReferences[0],
        resource,
        routeDocumentId,
        setDocument,
        setSelection,
      );
    }
  }, [
    resource,
    updateDocument,
    activeReferences,
    routeDocumentId,
    setDocument,
  ]);

  const selectReference = (ref: ContentReference) => {
    if (resource) {
      applyDocumentSelection(
        ref,
        resource,
        routeDocumentId,
        setDocument,
        setSelection,
      );
    }
  };

  const waitingForVersionChoice =
    selection === undefined &&
    activeReferences.length > 1 &&
    (!isSpaNavigation || forceChooseDocumentVersion);

  const versionChoiceRequired =
    forceChooseDocumentVersion && activeReferences.length > 1;

  const chooseReference = {
    references: activeReferences,
    selectReference,
    modalOpen: waitingForVersionChoice,
    isDocumentReady:
      documentState.document?.id === routeDocumentId &&
      !waitingForVersionChoice &&
      (!versionChoiceRequired || selection !== undefined),
  };

  return {
    ...(documentState.document ?? {}),
    icon: resource?.icon ?? documentState.icon,
    error,
    isFetching,
    chooseReference,
  };
};

export const useDocumentResource = (
  documentId: string | undefined,
  fetchResource: boolean,
) => {
  const { country: userCountry, language: userLanguage } =
    useAtomValue(userPrefsAtom);

  /**
   * if is Admin, Stakeholder or operator,
   * GetResource will ignore country in param and return all countries' resources
   **/

  const { data, error, ...rest } = useQuery(
    documentId && userLanguage && userCountry.id && fetchResource
      ? getResourceOptions({
          path: { id: documentId },
          query: { country: userCountry.id, language: userLanguage },
        })
      : {
          queryKey: getResourceQueryKey({
            path: { id: '' },
            query: { country: userCountry.id, language: userLanguage },
          }),
          queryFn: skipToken,
        },
  );

  return {
    resource: data,
    error,
    ...rest,
  };
};

export const useComplianceCourse = (courseId: string) => {
  const { data: complianceUrl, error } = useQuery({
    ...getSqsslOptions({ path: { courseId } }),
  });

  return {
    complianceUrl: complianceUrl?.url,
    error: error,
  };
};
